import React from 'react';
import {
	FormattedMessage,
	defineMessages,
} from 'react-intl';

import ConductorAuthFooter from '~/components/atoms/auth/ConductorAuthFooter';
import ConductorLoginLayout from '~/components/atoms/auth/ConductorLoginLayout';
import ConductorLogo from '~/components/atoms/auth/ConductorLogo';
import DisabledContent from '~/components/patterns/content/DisabledContent';
import HorizontalLine from '~/components/atoms/auth/HorizontalLine';
import LoginScreenEmailSentMessage from '~/components/app/LoginScreenEmailSentMessage';
import LoginScreenPasswordResetMessage from '~/components/app/LoginScreenPasswordResetMessage';
import LoginWithCredentialsForm from '~/components/app/LoginWithCredentialsForm';
import LoginWithGoogleForm from '~/components/app/LoginWithGoogleForm';
import LoginWithTwoFactorAuthenticationForm from '~/components/app/LoginWithTwoFactorAuthenticationForm';
import RequestPasswordResetForm from '~/components/app/RequestPasswordResetForm';
import ResetPasswordForm from '~/components/app/ResetPasswordForm';
import ResetPasswordTwoFactorAuthenticationForm from '~/components/app/ResetPasswordTwoFactorAuthenticationForm';

import useUrlState from '~/hooks/useUrlState';

import {
	LoginScreenView,
	isLoginScreenView,
} from '~/model/loginScreen';

import {
	navigate,
} from '~/routing/router';

import matchAndReturn from '~/utilities/matchAndReturn';



const messages = defineMessages({
	invitationResent: {
		id: 'ui.loginScreen.invitationResent',
	},
	lostPasswordSuccess: {
		id: 'ui.loginScreen.lostPasswordSuccess',
	},
	magicLinkResent: {
		id: 'ui.loginScreen.magicLinkResent',
	},
	newToConductor: {
		id: 'ui.loginScreen.newToConductor',
		defaultMessage: 'New to Conductor?',
	},
	tryForFree: {
		id: 'ui.loginScreen.tryForFree',
		defaultMessage: 'Try for free',
	},
	or: {
		id: 'ui.general.or',
	},
	resetPasswordSuccess: {
		id: 'ui.loginScreen.resetPasswordSuccess',
	},
});



const NewLoginScreen: React.FC = () => {
	const urlState = useUrlState();

	const [view, setView] = React.useState(
		() => isLoginScreenView(urlState.name)
			? urlState.name as LoginScreenView
			: LoginScreenView.Login,
	);

	const [defaultEmail, setDefaultEmail] = React.useState<string | undefined>(undefined);
	const [isDoingOAuthLogin, setDoingOAuthLogin] = React.useState(false);

	const [message, setMessage] = React.useState<
		'invitationResent' | 'magicLinkResent' | null
	>(null);

	React.useEffect(
		() => {
			function removeParam(name: string) {
				const params = { ...urlState.params };
				delete params[name];

				navigate(
					urlState.name,
					params,
					{
						replace: true,
					},
				);
			}

			if (urlState.params.email_sent) {
				setMessage('magicLinkResent');
				removeParam('email_sent');
			} else if (urlState.params.invitation_resent) {
				setMessage('invitationResent');
				removeParam('invitation_resent');
			}
		},
		[urlState],
	);

	const reset = React.useCallback(
		() => {
			setDefaultEmail(undefined);
			setDoingOAuthLogin(false);
			setMessage(null);
			setView(LoginScreenView.Login);
		},
		[],
	);

	function renderContent() {
		switch (message) {

			case 'magicLinkResent':
				return (
					<LoginScreenEmailSentMessage
						message={(
							<FormattedMessage {...messages.magicLinkResent} />
						)}
						onClick={reset}
					/>
				);

			case 'invitationResent':
				return (
					<LoginScreenEmailSentMessage
						message={(
							<FormattedMessage {...messages.invitationResent} />
						)}
						onClick={reset}
					/>
				);

		}

		const renderView = matchAndReturn(view, {
			[LoginScreenView.Login]: () => (
				<DisabledContent disabledContent={isDoingOAuthLogin}>
					<LoginWithCredentialsForm
						conductorStyle={true}
						defaultEmail={defaultEmail}
						isDisabled={isDoingOAuthLogin}
						onEmailChange={setDefaultEmail}
						setView={setView}
					/>
				</DisabledContent>
			),
			[LoginScreenView.LoginTwoFactorAuthentication]: () => (
				<LoginWithTwoFactorAuthenticationForm setView={setView} />
			),
			[LoginScreenView.PasswordLost]: () => (
				<DisabledContent disabledContent={isDoingOAuthLogin}>
					<RequestPasswordResetForm
						conductorStyle={true}
						defaultEmail={defaultEmail}
						isDisabled={isDoingOAuthLogin}
						onEmailChange={setDefaultEmail}
						setView={setView}
					/>
				</DisabledContent>
			),
			[LoginScreenView.PasswordLostRequested]: () => (
				<LoginScreenEmailSentMessage
					message={(
						<FormattedMessage {...messages.lostPasswordSuccess} />
					)}
					onClick={reset}
				/>
			),
			[LoginScreenView.PasswordReset]: () => (
				<DisabledContent disabledContent={isDoingOAuthLogin}>
					<ResetPasswordForm
						isDisabled={isDoingOAuthLogin}
						setView={setView}
					/>
				</DisabledContent>
			),
			[LoginScreenView.PasswordResetFinished]: () => (
				<LoginScreenPasswordResetMessage
					message={(
						<FormattedMessage {...messages.resetPasswordSuccess} />
					)}
					onClick={reset}
				/>
			),
			[LoginScreenView.PasswordResetTwoFactorAuthentication]: () => (
				<DisabledContent
					disabledContent={isDoingOAuthLogin}
				>
					<ResetPasswordTwoFactorAuthenticationForm
						isDisabled={isDoingOAuthLogin}
						setView={setView}
					/>
				</DisabledContent>
			),
		});

		return renderView();
	}

	const showOAuthSection = (
		view !== LoginScreenView.LoginTwoFactorAuthentication
	);

	const showNewToConductorLink = (
		view === LoginScreenView.Login
		&& message === null
	);

	return (
		<ConductorLoginLayout
			footer={(
				<ConductorAuthFooter />
			)}
		>
			<div className="conductor-login-card__logo">
				<ConductorLogo href="https://www.conductor.com" />
			</div>

			{renderContent()}

			{showOAuthSection && (
				<>
					<div className="conductor-login-card__divider">
						<HorizontalLine>
							<FormattedMessage {...messages.or} />
						</HorizontalLine>
					</div>

					<LoginWithGoogleForm
						conductorStyle={true}
						onLoginFinish={() => setDoingOAuthLogin(false)}
						onLoginStart={() => setDoingOAuthLogin(true)}
						setView={setView}
					/>
				</>
			)}

			{showNewToConductorLink && (
				<div className="conductor-login-card__new-to-conductor">
					<FormattedMessage {...messages.newToConductor} />
					{' '}
					<a
						href="https://www.conductor.com/free-trial/"
						rel="noopener noreferrer"
						target="_blank"
					>
						<FormattedMessage {...messages.tryForFree} />
					</a>
				</div>
			)}
		</ConductorLoginLayout>
	);
};



export default NewLoginScreen;
